import type { IJsonApiResponse } from "@/interfaces/IJsonApiResponse";
import Model from "./Model";
import { Jsona } from "jsona";
import type { TJsonApiBody, TJsonApiData } from "jsona/lib/JsonaTypes";
import type IJsonApiPageMeta from "~/interfaces/IJsonApiPageMeta";
import type { ICustomer } from "./Customer";
import type { IProduct } from "./Product";
import Customer from "./Customer";
import Product from "./Product";
import auth from "~/services/AuthService";

export interface IProductReview {
  id: string | null;
  customer_name: string | null;
  rating: number;
  opinion: string | null;
  short_opinion: string | null;
  created_utc: string | null;
  created_at_text?: string;
  is_approved?: boolean;
  verified_purchase?: boolean;
  review_status?: string;
  captcha_token?: string | null;
  customer?: ICustomer;
  product?: IProduct;
}

export interface IProductReviewState {
  user_can_review: boolean;
  can_review: boolean;
  has_review: boolean;
  review_id: string | null;
  review_status: string;
  reason: string;
  has_verified_purchase: boolean;
  requires_purchase_validation: boolean;
  requires_captcha: boolean;
}

export default class ProductReview extends Model implements IProductReview {
  public type: string = "product-reviews";
  public relationshipNames: string[] = ["product", "customer"];

  public id: string | null = null;
  public customer_name: string | null = null;
  public rating: number = 0;
  public opinion: string | null = null;
  public short_opinion: string | null = null;
  public created_utc: string | null = null;
  public created_at_text?: string;
  public is_approved: boolean = false;
  public verified_purchase: boolean = false;
  public review_status: string = "none";
  public captcha_token?: string | null;
  public customer?: Customer;
  public product?: Product;

  public constructor({
    id,
    customer_name,
    rating,
    opinion,
    short_opinion,
    created_utc,
    created_at_text,
    is_approved,
    verified_purchase,
    review_status,
    captcha_token,
    customer,
    product,
  }: IProductReview) {
    super();

    this.id = id || null;
    this.customer_name = customer_name || null;
    this.rating = rating || 0;
    this.opinion = opinion || null;
    this.short_opinion = short_opinion || null;
    this.created_utc = created_utc || null;
    this.created_at_text = created_at_text;
    this.is_approved = !!is_approved;
    this.verified_purchase = !!verified_purchase;
    this.review_status =
      review_status || (this.is_approved ? "approved" : "none");
    this.captcha_token = captcha_token || null;
    this.customer = customer ? new Customer(customer) : undefined;
    this.product = product ? new Product(product) : undefined;
  }

  public static getDefaultQueryFields() {
    return {
      "fields[product-reviews]":
        "customer_name,rating,opinion,short_opinion,created_utc,created_at_text,is_approved,verified_purchase,review_status",
    };
  }

  public static getDefaultState(): IProductReviewState {
    return {
      user_can_review: false,
      can_review: false,
      has_review: false,
      review_id: null,
      review_status: "none",
      reason: "auth_required",
      has_verified_purchase: false,
      requires_purchase_validation: true,
      requires_captcha: false,
    };
  }

  public static async fetchByProduct(productId: string, page: number = 1) {
    const path = `/shop-v1/products/${productId}/reviews`;
    const query = {
      "page[number]": page.toString(),
      "page[size]": "5",
      sort: "-id",
      ...ProductReview.getDefaultQueryFields(),
    };
    const queryStr = new URLSearchParams(query).toString();
    const url = path + "?" + queryStr;
    const data = await $fetch<IJsonApiResponse>(url, {
      baseURL: import.meta.env.VITE_API_ENDPOINT,
      headers: {
        Accept: "application/vnd.api+json",
        "Content-Type": "application/vnd.api+json",
      },
    });
    const jsona = new Jsona();
    const jsonapi = jsona.deserialize(data as TJsonApiBody);
    const instances = ProductReview.fromArray(jsonapi as IProductReview[]);
    const meta = data.meta as IJsonApiPageMeta | undefined;
    return { data: instances, meta };
  }

  public static async fetchOwnByProduct(productId: string) {
    const { $apiFetch } = useNuxtApp();
    const queryStr = new URLSearchParams({
      "filter[customer_id]": String(auth.getUser()?.id || ""),
      ...ProductReview.getDefaultQueryFields(),
    }).toString();
    const doc = await $apiFetch<IJsonApiResponse>(
      `/shop-v1/products/${productId}/reviews?${queryStr}`,
    );
    const jsona = new Jsona();
    const items = jsona.deserialize(doc) as IProductReview[];
    return items.length && items[0] ? new ProductReview(items[0]) : undefined;
  }

  public static async fetchStateByProduct(productId: string) {
    const { $apiFetch } = useNuxtApp();
    const doc = await $apiFetch<IJsonApiResponse>(
      `/shop-v1/products/${productId}/-actions/user-can-review`,
    );
    return {
      ...ProductReview.getDefaultState(),
      ...(doc?.meta as Partial<IProductReviewState> | undefined),
    } as IProductReviewState;
  }

  public async save(captchaToken?: string) {
    const { $apiFetch } = useNuxtApp();
    const path = `/shop-v1/product-reviews` + (this.id ? `/${this.id}` : "");
    const query = {
      ...ProductReview.getDefaultQueryFields(),
    };
    const queryStr = new URLSearchParams(query).toString();
    const url = path + "?" + queryStr;
    const jsona = new Jsona();
    const input = jsona.serialize({ stuff: this });
    if (!this.id) {
      delete (input.data as TJsonApiData).id;
    }
    if (captchaToken) {
      (input.data as TJsonApiData).attributes = {
        ...((input.data as TJsonApiData).attributes || {}),
        captcha_token: captchaToken,
      };
    }
    const data = await $apiFetch<IJsonApiResponse>(url, {
      method: this.id ? "PATCH" : "POST",
      body: JSON.stringify(input),
    });
    const doc = jsona.deserialize(data as TJsonApiBody) as IProductReview;
    Object.assign(this, new ProductReview(doc));
  }

  public async delete() {
    const { $apiFetch } = useNuxtApp();
    if (!this.id) {
      return;
    }
    const url = `/shop-v1/product-reviews/${this.id}`;
    await $apiFetch(url, {
      method: "DELETE",
    });
  }

  public static fromArray(items: IProductReview[]): ProductReview[] {
    return items.map((item: IProductReview) => new ProductReview(item));
  }

  public getCreatedDate() {
    if (!this.created_utc) {
      return null;
    }
    const date = new Date(this.created_utc);
    if (!(date instanceof Date)) {
      return null;
    }
    const Y = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${Y}-${m}-${d}`;
  }
}
